import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { Wallet, Trash2 } from 'lucide-react';
import { deleteToRecycleBin } from './recycleBinUtil';

const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));

// Several snapshots often land on the same date, so the row has to show the clock
// time it was saved to tell them apart.
const savedAtText = (entry) => {
  const d = entry.createdAt?.toDate ? entry.createdAt.toDate() : null;
  return d ? d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyForm = () => ({
  date: todayStr(),
  paymentDue: '',
  preparingForInvoice: '',
  paymentInReview: '',
  paymentInvoiced: '',
  note: ''
});

// Pathao (and any other courier) never exposes payment/invoice numbers through their
// public API -- confirmed by probing it directly, every guessed endpoint returns the
// same generic "Unauthorized" fallback. So this page doesn't try to compute or fetch
// money figures itself; it just gives Sohan a dated place to copy in the 4 numbers from
// Pathao's own "Invoices > Payment-Processed" page (Payment Due / Preparing for Invoice /
// Payment in Review / Payment Invoiced -- the 4 stages an order's money passes through
// before it lands in his bank), so he has one history he can check without logging into
// Pathao every time.
const CourierLedger = () => {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Ordered by when it was saved, not the date typed in -- several snapshots get taken
  // on the same day and the newest has to sit on top.
  useEffect(() => {
    const q = query(collection(db, 'courierLedger'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, []);

  const saveEntry = async () => {
    if (!form.date) return alert('Please pick a date.');
    setSaving(true);
    try {
      await addDoc(collection(db, 'courierLedger'), {
        date: form.date,
        paymentDue: Number(form.paymentDue) || 0,
        preparingForInvoice: Number(form.preparingForInvoice) || 0,
        paymentInReview: Number(form.paymentInReview) || 0,
        paymentInvoiced: Number(form.paymentInvoiced) || 0,
        note: form.note || '',
        createdAt: serverTimestamp()
      });
      setForm(emptyForm());
    } catch (e) {
      alert('Could not save: ' + e.message);
    }
    setSaving(false);
  };

  const removeEntry = async (entry) => {
    if (!window.confirm(`Delete the snapshot dated ${entry.date}? You can restore it from the Recycle Bin.`)) return;
    await deleteToRecycleBin({ collectionName: 'courierLedger', id: entry.id, data: entry, label: 'Courier Payment Snapshot' });
  };

  const latest = entries[0];
  const totalPending = (e) => (Number(e.paymentDue) || 0) + (Number(e.preparingForInvoice) || 0) + (Number(e.paymentInReview) || 0) + (Number(e.paymentInvoiced) || 0);

  return (
    <div>
      <p style={{ fontSize: '13px', color: '#666', marginTop: 0 }}>
        Pathao doesn't let any app pull payment numbers automatically -- this only exists on Pathao's own dashboard
        (<b>Invoices → Payment-Processed</b>). So whenever you check that page, copy its 4 tab totals here with today's date to keep a running history inside Kizuna Inventory.
      </p>

      {latest && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {[
            { label: 'Payment Due', value: latest.paymentDue, color: '#c0392b' },
            { label: 'Preparing for Invoice', value: latest.preparingForInvoice, color: '#8a6d3b' },
            { label: 'Payment in Review', value: latest.paymentInReview, color: '#8a6d3b' },
            { label: 'Payment Invoiced', value: latest.paymentInvoiced, color: '#218c61' },
            { label: 'Total Still Coming To You', value: totalPending(latest), color: '#4a00e0' }
          ].map((c) => (
            <div className="hover-lift" key={c.label} style={{ flex: '1 1 200px', background: '#f8f9ff', borderRadius: '14px', padding: '14px 18px' }}>
              <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>{c.label}</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: c.color }}>৳ {formatTk(c.value)}</div>
            </div>
          ))}
          <div style={{ flexBasis: '100%', fontSize: '11px', color: '#999' }}>as of {latest.date}{savedAtText(latest) && ` · saved ${savedAtText(latest)}`}</div>
        </div>
      )}

      <div className="white-box" style={{ padding: '18px 20px', marginBottom: '20px', background: '#fff', border: '1px solid #eee' }}>
        <b style={{ fontSize: '13px', color: '#666' }}>Add a snapshot from Pathao's Invoices → Payment-Processed page</b>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
          <label style={{ fontSize: '11px', color: '#888' }}>
            Date<br />
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-field" style={{ marginBottom: 0 }} />
          </label>
          <label style={{ fontSize: '11px', color: '#888' }}>
            Payment Due (৳)<br />
            <input type="number" value={form.paymentDue} onFocus={(e) => e.target.select()} onChange={(e) => setForm({ ...form, paymentDue: e.target.value })} className="input-field" style={{ marginBottom: 0, width: '140px' }} />
          </label>
          <label style={{ fontSize: '11px', color: '#888' }}>
            Preparing for Invoice (৳)<br />
            <input type="number" value={form.preparingForInvoice} onFocus={(e) => e.target.select()} onChange={(e) => setForm({ ...form, preparingForInvoice: e.target.value })} className="input-field" style={{ marginBottom: 0, width: '140px' }} />
          </label>
          <label style={{ fontSize: '11px', color: '#888' }}>
            Payment in Review (৳)<br />
            <input type="number" value={form.paymentInReview} onFocus={(e) => e.target.select()} onChange={(e) => setForm({ ...form, paymentInReview: e.target.value })} className="input-field" style={{ marginBottom: 0, width: '140px' }} />
          </label>
          <label style={{ fontSize: '11px', color: '#888' }}>
            Payment Invoiced (৳)<br />
            <input type="number" value={form.paymentInvoiced} onFocus={(e) => e.target.select()} onChange={(e) => setForm({ ...form, paymentInvoiced: e.target.value })} className="input-field" style={{ marginBottom: 0, width: '140px' }} />
          </label>
          <label style={{ fontSize: '11px', color: '#888', flex: '1 1 200px' }}>
            Note (optional)<br />
            <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="input-field" style={{ marginBottom: 0 }} placeholder="e.g. checked after InstaPay withdraw" />
          </label>
        </div>
        <button onClick={saveEntry} disabled={saving} className="btn-confirm" style={{ marginTop: '12px', padding: '10px 20px', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving...' : 'Save Snapshot'}
        </button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
            <th style={{ padding: '10px' }}>Date</th>
            <th>Payment Due</th>
            <th>Preparing</th>
            <th>In Review</th>
            <th>Invoiced</th>
            <th>Total Pending</th>
            <th>Note</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
              <td style={{ padding: '10px', fontWeight: 'bold' }}>
                {e.date}
                {savedAtText(e) && <div style={{ fontSize: '10px', color: '#999', fontWeight: 'normal' }}>{savedAtText(e)}</div>}
              </td>
              <td style={{ color: '#c0392b' }}>৳ {formatTk(e.paymentDue)}</td>
              <td style={{ color: '#8a6d3b' }}>৳ {formatTk(e.preparingForInvoice)}</td>
              <td style={{ color: '#8a6d3b' }}>৳ {formatTk(e.paymentInReview)}</td>
              <td style={{ color: '#218c61' }}>৳ {formatTk(e.paymentInvoiced)}</td>
              <td style={{ fontWeight: 'bold', color: '#4a00e0' }}>৳ {formatTk(totalPending(e))}</td>
              <td style={{ fontSize: '12px', color: '#888' }}>{e.note}</td>
              <td>
                <button onClick={() => removeEntry(e)} title="Delete" style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#c0392b' }}>
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr><td colSpan={8} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
              <Wallet size={20} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              No snapshots saved yet.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CourierLedger;
